import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Progress,
  Badge,
  theme,
  Empty,
  Modal,
  message,
} from "antd";
import {
  ThunderboltOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";
import dayjs from "dayjs";

const { Title, Text } = Typography;

export default function CurrentDispatchList() {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch running dispatches
  const fetchDispatches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch("/api/dispatch/list?status=running,paused");
      const data = await response.json();
      if (data.success) {
        setDispatches(data.data?.dispatches || []);
      }
    } catch (error) {
      console.error("Failed to fetch dispatches:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDispatches();
    // Poll every 30 seconds for live updates
    const interval = setInterval(fetchDispatches, 30000);
    return () => clearInterval(interval);
  }, [fetchDispatches]);

  // Stop dispatch handler
  const handleStopDispatch = useCallback((dispatch) => {
    Modal.confirm({
      title: "Stop Dispatch",
      content: (
        <div>
          <p>Are you sure you want to stop this dispatch?</p>
          <p><strong>ID:</strong> {dispatch.id}</p>
          <p><strong>Control Group:</strong> {dispatch.controlGroupName}</p>
          <p style={{ color: "#faad14", marginTop: 12 }}>
            This action cannot be undone. The dispatch will be marked as cancelled.
          </p>
        </div>
      ),
      okText: "Stop Dispatch",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await authenticatedFetch(`/api/dispatch/${dispatch.id}/stop`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });
          const data = await response.json();
          if (data.success) {
            message.success(`Dispatch ${dispatch.id} stopped successfully`);
            fetchDispatches(); // Refresh the list
          } else {
            message.error(data.message || "Failed to stop dispatch");
          }
        } catch (error) {
          console.error("Failed to stop dispatch:", error);
          message.error("Failed to stop dispatch");
        }
      },
    });
  }, [fetchDispatches]);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "15%",
      render: (id, record) => (
        <Space direction="vertical" size={2}>
          <Space>
            <Badge status={record.status === "paused" ? "warning" : "processing"} />
            <Text strong>{id}</Text>
          </Space>
          {record.status === "paused" && <Tag color="warning">Paused</Tag>}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "dispatchType",
      key: "dispatchType",
      width: "12%",
      align: "center",
      render: (type) => {
        const colors = {
          "FCAS_RAISE": "blue",
          "FCAS_LOWER": "green",
          "PEAK_SHAVING": "orange",
          "ARBITRAGE": "purple",
          "MANUAL": "default",
        };
        return <Tag color={colors[type] || "default"}>{type || "MANUAL"}</Tag>;
      },
    },
    {
      title: "Control Group",
      dataIndex: "controlGroupName",
      key: "controlGroupName",
      width: "16%",
      align: "center",
      ellipsis: true,
    },
    {
      title: "Started",
      key: "started",
      width: "14%",
      align: "center",
      render: (_, record) => {
        const start = record.actualStart || record.scheduledStart;
        return start ? dayjs(start).format("HH:mm:ss") : "-";
      },
    },
    {
      title: "Power",
      key: "power",
      width: "15%",
      align: "center",
      render: (_, record) => {
        const target = record.targetPowerKw || 0;
        const actual = record.actualPowerKw || 0;
        const percent = target ? Math.abs(actual / target) * 100 : 0;
        return (
          <Space direction="vertical" size={0}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {Number(actual).toLocaleString()} / {Number(target).toLocaleString()} kW
            </Text>
            <Progress
              percent={Math.min(percent, 100)}
              size="small"
              showInfo={false}
              strokeColor={target < 0 ? token.colorWarning : token.colorSuccess}
            />
          </Space>
        );
      },
    },
    {
      title: "Devices",
      key: "devices",
      width: "12%",
      align: "center",
      render: (_, record) => {
        const responding = record.participatingDevices || 0;
        const total = record.totalDevices || 0;
        const pct = total ? ((responding / total) * 100).toFixed(0) : 0;
        return (
          <Space>
            <Text>{responding}/{total}</Text>
            <Tag color={responding === total ? "success" : "warning"}>
              {pct}%
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "16%",
      align: "center",
      render: (_, record) => (
        <Space size={8}>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/dispatch/current/${record.id}`)}
          >
            View
          </Button>
          <Button
            size="small"
            danger
            icon={<PauseCircleOutlined />}
            onClick={() => handleStopDispatch(record)}
          >
            Stop
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout active="dispatchCurrent">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              <Badge status="processing" />
              <span style={{ marginLeft: 8 }}>Current Dispatches</span>
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Live monitoring of active dispatch operations
            </Text>
          </div>
          <Space>
            <Text type="secondary">
              {dispatches.length} active dispatch{dispatches.length !== 1 ? "es" : ""}
            </Text>
            <Button icon={<ReloadOutlined />} onClick={fetchDispatches} loading={loading}>
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      {/* Dispatch List */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <ThunderboltOutlined style={{ color: token.colorPrimary }} />
            <Text strong>Active Dispatches</Text>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={dispatches}
          rowKey="id"
          pagination={false}
          size="middle"
          loading={loading}
          locale={{
            emptyText: <Empty description="No active dispatches" />,
          }}
        />
      </Card>
    </PageLayout>
  );
}
